import { t } from '@lingui/core/macro';
import { X } from '@untitledui/icons/X';

import type { AgeGroup } from '@/api/core-api/types.gen';

import { Button } from '@/components/base/buttons/button';
import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';

import { AGE_GROUP_OPTIONS } from '../../-launch/drawers/target-audience/age-range';

export const PARTICIPANT_AGE_FILTER_VALUES = [
  '18-20',
  '21-29',
  '30-39',
  '40-54',
  '55+',
] as const satisfies readonly AgeGroup[];

export type ParticipantAgeFilter =
  (typeof PARTICIPANT_AGE_FILTER_VALUES)[number];

export function nextSearchOnAgeFilterChange<
  T extends {
    page?: number;
    ageGroups?: AgeGroup[] | undefined;
  },
>(prev: T, next: ReadonlySet<AgeGroup>): T {
  return {
    ...prev,
    ageGroups: next.size === 0 ? undefined : [...next],
    page: 1,
  };
}

interface ParticipantAgeFilterProps {
  value: ReadonlySet<AgeGroup>;
  onChange: (next: Set<AgeGroup>) => void;
}

export function ParticipantAgeFilter({
  value,
  onChange,
}: ParticipantAgeFilterProps) {
  const labels = [...AGE_GROUP_OPTIONS].map((option) => ({
    id: option.id,
    label: option.label,
  }));

  return (
    <MultiSelectDropdown<AgeGroup>
      size="xs"
      items={labels}
      selectedKeys={value}
      onSelectionChange={onChange}
      triggerVariant="button"
      showCheckboxes
      placeholder={t`All ages`}
      triggerPrefix={t`Age`}
      maxDisplay={2}
      noWrap
      matchTriggerWidth={false}
      popoverPlacement="bottom start"
      popoverClassName="min-w-[180px]"
      aria-label={t`Filter by age bracket`}
      footer={({ clearAll, close }) => (
        <Button
          color="tertiary"
          size="xs"
          iconLeading={X}
          className="w-full justify-start gap-2 px-3"
          onPress={() => {
            clearAll();
            close();
          }}
        >
          {t`Clear selection`}
        </Button>
      )}
    />
  );
}
